import React, { useState, useEffect } from "react";
import { apiClient } from "../../App";
import { toast } from "sonner";
import { Sparkles, CheckCircle, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { priorityColors } from "./ComplianceScoreRing";

export function AiExtractionPanel({ contracts, onComplete }) {
  const [contractId, setContractId] = useState("");
  const [extracting, setExtracting] = useState(false);
  const [taskId, setTaskId] = useState(null);
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);

  const startExtraction = async () => {
    if (!contractId) { toast.error("Select a contract"); return; }
    setExtracting(true); setResult(null);
    try {
      const res = await apiClient.post("/ai/extract-obligations", { contract_id: contractId });
      setTaskId(res.data.task_id);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to start extraction"); setExtracting(false); }
  };

  useEffect(() => {
    if (!taskId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await apiClient.get(`/ai/tasks/${taskId}`);
        if (cancelled) return;
        if (res.data.status === "completed") { setResult(res.data.result); setExtracting(false); setTaskId(null); toast.success("Obligations extracted"); }
        else if (res.data.status === "failed") { toast.error(res.data.error || "Failed"); setExtracting(false); setTaskId(null); }
        else { setTimeout(poll, 2000); }
      } catch { if (!cancelled) setTimeout(poll, 3000); }
    };
    poll();
    return () => { cancelled = true; };
  }, [taskId]);

  const saveExtracted = async () => {
    if (!result) return;
    setSaving(true);
    try {
      const obPayloads = result.obligations.map(o => ({
        contract_id: contractId, title: o.title, description: o.description || "",
        responsible_party: o.responsible_party || "internal", due_date: o.due_date || "",
        priority: o.priority || "medium", source: "ai_extracted", compliance_status: "pending_review",
      }));
      const msPayloads = result.milestones.map(m => ({
        contract_id: contractId, title: m.title, description: m.description || "",
        due_date: m.due_date || "", source: "ai_extracted",
      }));
      if (obPayloads.length > 0) await apiClient.post("/obligations/bulk", obPayloads);
      if (msPayloads.length > 0) await apiClient.post("/milestones/bulk", msPayloads);
      toast.success(`Saved ${obPayloads.length} obligations and ${msPayloads.length} milestones`);
      setResult(null); setContractId(""); onComplete();
    } catch { toast.error("Failed to save extracted items"); }
    finally { setSaving(false); }
  };

  const selectedContract = contracts.find(c => c.id === contractId);

  return (
    <Card className="border-violet-200 bg-violet-50/30" data-testid="ai-extraction-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
          <Sparkles size={18} className="text-violet-600" /> AI Obligation Extraction
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-end gap-3">
          <div className="flex-1 space-y-1">
            <Label className="text-xs">Select Contract</Label>
            <Select value={contractId} onValueChange={setContractId}>
              <SelectTrigger className="rounded-sm" data-testid="ai-contract-select"><SelectValue placeholder="Choose a contract to analyze..." /></SelectTrigger>
              <SelectContent>{contracts.map(c => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button onClick={startExtraction} disabled={extracting || !contractId} className="rounded-sm gap-2 bg-violet-600 hover:bg-violet-700" data-testid="extract-obligations-btn">
            {extracting ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
            {extracting ? "Analyzing..." : "Extract Obligations"}
          </Button>
        </div>
        {extracting && (
          <div className="flex items-center gap-3 p-4 bg-white rounded-sm border border-violet-200">
            <Loader2 size={20} className="animate-spin text-violet-600" />
            <div><p className="text-sm font-medium">AI is analyzing the contract...</p><p className="text-xs text-slate-500">This may take 15-30 seconds</p></div>
          </div>
        )}
        {result && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">
                Found <span className="text-violet-700">{result.obligations.length} obligations</span> and <span className="text-violet-700">{result.milestones.length} milestones</span>
                {selectedContract && <span className="text-slate-500"> in "{selectedContract.title}"</span>}
              </p>
              <Button onClick={saveExtracted} disabled={saving} className="rounded-sm gap-2 bg-violet-600 hover:bg-violet-700" data-testid="save-extracted-btn">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />} Save All
              </Button>
            </div>
            {result.obligations.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Obligations</p>
                {result.obligations.map(o => (
                  <div key={o.id} className="p-3 bg-white border rounded-sm text-sm">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{o.title}</span>
                      <Badge className={`text-xs rounded-sm ${priorityColors[o.priority || "medium"]}`}>{o.priority || "medium"}</Badge>
                      <Badge className="text-xs rounded-sm bg-slate-100 text-slate-600 capitalize">{o.responsible_party || "internal"}</Badge>
                    </div>
                    {o.description && <p className="text-xs text-slate-500 mt-1">{o.description}</p>}
                    {o.due_date && <p className="text-xs text-slate-400 mt-1">Due: {o.due_date}</p>}
                  </div>
                ))}
              </div>
            )}
            {result.milestones.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Milestones</p>
                {result.milestones.map(m => (
                  <div key={m.id} className="p-3 bg-white border rounded-sm text-sm">
                    <span className="font-medium">{m.title}</span>
                    {m.description && <p className="text-xs text-slate-500 mt-1">{m.description}</p>}
                    {m.due_date && <p className="text-xs text-slate-400 mt-1">Due: {m.due_date}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
